import React, { useEffect, useState } from "react";
import { Alert, Card, Form, Spinner, Table } from "react-bootstrap";
import {
  getCountries,
  getTrialScreenerAccessByCountry,
  updateTrialScreenerAccess,
} from "../services/APIClient";

const COLUMNS = [
  { key: "baseline", label: "Baseline" },
  { key: "midpoint", label: "Midpoint" },
  { key: "endOfYear", label: "End of Year" },
];

function TrialScreenerAccess() {
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    getCountries()
      .then((result) => setCountries(Array.isArray(result) ? result : []))
      .catch(() => setError("Failed to load territories."));
  }, []);

  useEffect(() => {
    if (!countryId) {
      setConfiguration(null);
      return;
    }

    setLoading(true);
    setError(null);
    getTrialScreenerAccessByCountry(countryId)
      .then(setConfiguration)
      .catch(() => {
        setConfiguration(null);
        setError("Failed to load trial screener access.");
      })
      .finally(() => setLoading(false));
  }, [countryId]);

  const updateMatchingCells = (assessmentId, enabled) => {
    setConfiguration((current) => ({
      ...current,
      grades: current.grades.map((grade) => ({
        ...grade,
        cells: Object.fromEntries(
          Object.entries(grade.cells).map(([key, cell]) => [
            key,
            {
              ...cell,
              assessments: cell.assessments.map((assessment) =>
                assessment.assessmentId === assessmentId
                  ? { ...assessment, trialEnabled: enabled }
                  : assessment
              ),
            },
          ])
        ),
      })),
    }));
  };

  const toggleAssessment = async (assessmentId, enabled) => {
    setError(null);
    setSavingIds((current) => new Set(current).add(assessmentId));
    updateMatchingCells(assessmentId, enabled);
    try {
      await updateTrialScreenerAccess(countryId, assessmentId, enabled);
    } catch (requestError) {
      updateMatchingCells(assessmentId, !enabled);
      setError("Failed to save trial screener access.");
    } finally {
      setSavingIds((current) => {
        const next = new Set(current);
        next.delete(assessmentId);
        return next;
      });
    }
  };

  return (
    <Card>
      <Card.Header>Trial Screener Access</Card.Header>
      <Card.Body>
        <Form.Group className="mb-4" controlId="trial-screener-country">
          <Form.Label>Territory</Form.Label>
          <Form.Select
            value={countryId}
            onChange={(event) => setCountryId(event.target.value)}
          >
            <option value="">Select a territory</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <Spinner animation="border" role="status" />}

        {!loading && configuration && (
          <Table bordered responsive>
            <thead>
              <tr>
                <th>Grade</th>
                {COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {configuration.grades.map((grade) => (
                <tr key={grade.gradeLevel}>
                  <th scope="row">{grade.gradeName}</th>
                  {COLUMNS.map((column) => {
                    const cell = grade.cells[column.key];
                    return (
                      <td key={column.key}>
                        {cell.assessments.map((assessment) => (
                          <div key={assessment.assessmentId} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label={assessment.assessmentName}
                              aria-label={`${grade.gradeName} ${column.label} ${assessment.assessmentName}`}
                              checked={assessment.trialEnabled === true}
                              disabled={savingIds.has(assessment.assessmentId)}
                              onChange={(event) =>
                                toggleAssessment(
                                  assessment.assessmentId,
                                  event.target.checked
                                )
                              }
                            />
                            {!assessment.normalAvailability && (
                              <div className="small text-muted">
                                Not normally available
                              </div>
                            )}
                          </div>
                        ))}
                        {cell.configurationError && (
                          <div className="small text-danger">
                            {cell.configurationError}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default TrialScreenerAccess;
